import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import FilterListIcon from "@mui/icons-material/FilterList";
import SwapHorizonOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { PricingPortalAPI } from "../../../apis/api-index";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import EmailIcon from '@mui/icons-material/Email';
import {
  DataGridTable, FormPage, GridRowAction, notifyToast,
  ToastType,
} from "../../../components-old/ComponentsIndex";
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import { selectSessionConfig } from "../../../store/store-selector";
import { AppUtility } from "../../../utils/utility";
import { AuthAPI } from "../../../apis/auth-api";
import { Box } from "@mui/material";
import { IsLoggedInUserPricingApprover, IsLoggedInUserPricingAdmin } from "../../../hoc/withAuthRedirect";
import { selectUser } from "../../../store/store-selector";
import { Tooltip } from "@mui/material";
import SendEmailPopup from "../../../components/app-components/send-email-popup/SendEmailPopup"
import { sendEmailToApprover } from "../api-handler/api-handler";


const PricingChangesListForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);
  const user = useSelector(selectUser);

  /**************************************************
  //Form State variables
  **************************************************/
  const [pricingChangesInfoRecordList, setPricingChangesInfoRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [gridPageSizeOptions] = useState([10, 20, 50]);
  const [totalPricingChangesInfoRowCount, setTotalPricingChangesInfoRowCount] = useState(0);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [selectedToDate, setSelectedToDate] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);


  /**************************************************
  //Event definitions
  **************************************************/

  //Data grid Page Size Change event
  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  //onclick of confirm of delete popup
  const handleConfirm = async (selectedOption) => {
    await sendEmail(selectedOption);
    setPopupOpen(false);
  };

  //on click of cancel of send mail popup
  const handleCancel = () => {
    setPopupOpen(false);
  };

  //onClick of send mail popup
  const showSendEmailPopup = (event) => {
    try {
      setPopupOpen(true);
    } catch (err) {
      alert(err);
    }
  };

  //API to send email

  const sendEmail = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);
      const status = await sendEmailToApprover(param, {
        email: {
          loggedInUser: email
        },
        settingsConfig
      });
      if (status?.responseStatus === 200) {
        notifyToast("Email sent", ToastType.SUCCESS);
        spinnerCtx.setLoadingIndicator(false);
      } else {
        notifyToast(status.errorMessage ? status.errorMessage : "Email sent failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (e) {
      alert(e);
    }
  };

  //show review modal
  const handleShowSelectedPricingChangesDetails = (event) => {
    let _navigationData = { previousPath: "/pricingchanges" };
    navigate(
      {
        pathname: "/reviewdetails",
        search: createSearchParams({
          reviewFilter: `&auditid=${event.AuditId}`
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  const isLoggedInUserApprover = IsLoggedInUserPricingApprover();
  const isLoggedInUserAdmin = IsLoggedInUserPricingAdmin();

  const email = user.userRecord.email;

  //clearing date filter
  const onClearFilter = async () => {
    spinnerCtx.setLoadingIndicator(true);
    setSelectedFromDate(null);
    setSelectedToDate(null);
    setShowDateFilter(false);
    let pricingChangesList = isLoggedInUserApprover ? await PricingPortalAPI.getListWithPagination(
      settingsConfig.businessCode,
      settingsConfig.countryCode,
      "getAuditInfoList",
      "auditinfovantageportal")
      : await PricingPortalAPI.getListWithPagination(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getAuditInfoList",
        "auditinfovantageportal",
        `&useremail=${email}`);
    setPricingChangesInfoList(pricingChangesList);
  };

  //applying date filter
  const onApplyDateFilter = async (fromDate, toDate) => {
    spinnerCtx.setLoadingIndicator(true);
    let _curUser = await AuthAPI.validateCurrentUser();
    let email = _curUser.email;
    setSelectedFromDate(fromDate);
    setSelectedToDate(toDate);
    setShowDateFilter(false);
    let filterClause = isLoggedInUserApprover ? `&fromDate=${fromDate}&toDate=${toDate}` : `&fromDate=${fromDate}&toDate=${toDate}&useremail=${email}`;

    const filteredList = await PricingPortalAPI.getListWithPagination(
      settingsConfig.businessCode,
      settingsConfig.countryCode,
      "getAuditInfoList",
      "auditinfovantageportal",
      filterClause
    );
    setPricingChangesInfoList(filteredList);
  };

  //setting data in list
  const setPricingChangesInfoList = (recordlist) => {
    try {
      if (recordlist.length) recordlist.sort((a, b) => new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime());
      let _recordList = recordlist.map((item) => {
        return {
          id: item.AuditId,
          pricingChangesId: item.AuditId,
          location: item.Location,
          status: item.Status === t("pricingChanges.modified") ? t("pricingChanges.pending") : item.Status,
          pricingChangesAction: item.Action,
          description: item.ShortDesc,
          actionBy: item.UserEmail ? item.UserEmail.split("@")[0] : "",
          approver: item.ModifiedBy ? item.ModifiedBy.split("@")[0] : "",
          createdDate: new Date(item.CreatedDate).toLocaleString("en-GB").replace(",", ""),
          recordResponse: item,
        };
      });

      setPricingChangesInfoRecordList(_recordList);
      setTotalPricingChangesInfoRowCount(_recordList.length);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };
  /**************************************************
  //Form on load useEffect
  **************************************************/

  useEffect(() => {

    async function fetchData() {
      if (isLoggedInUserApprover) {
        const PricingChangesList = await PricingPortalAPI.getListWithPagination(
          settingsConfig.businessCode,
          settingsConfig.countryCode,
          "getAuditInfoList",
          "auditinfovantageportal");
        if (PricingChangesList && PricingChangesList.length > 0) {
          setPricingChangesInfoList(PricingChangesList);
        }
      } else {
        const PricingChangesList = await PricingPortalAPI.getListWithPagination(
          settingsConfig.businessCode,
          settingsConfig.countryCode,
          "getAuditInfoList",
          "auditinfovantageportal",
          `&useremail=${email}`);
        if (PricingChangesList && PricingChangesList.length > 0) {
          setPricingChangesInfoList(PricingChangesList);
        }
      }
    }

    spinnerCtx.setLoadingIndicator(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**************************************************
  //JSX delcaration
  **************************************************/
  const buttonConfigs = [
    {
      buttonText: <FilterListIcon />,
      buttonType: "filled",
      onClick: () => {
        setShowDateFilter(!showDateFilter);
      },
      buttonColor: "#A62B23",
      textColor: "#fff",
    },
    {
      buttonText: <SystemUpdateAltIcon />,
      buttonType: "outlined",
      onClick: () => {
        AppUtility.handleDownload(pricingChangesInfoRecordList, "PricingChangesInfo.xlsx");
      },
      buttonColor: "#A62B23",
      textColor: "#A62B23",
    },
    ...(isLoggedInUserAdmin
      ? [
        {
          buttonText: (
            <Tooltip title="Send email to approver" arrow>
              <EmailIcon />
            </Tooltip>
          ),
          buttonType: "filled",
          onClick: showSendEmailPopup,
          buttonColor: "#A62B23",
          textColor: "#fff",
        },
      ]
      : []),
  ];

  const dataGridSchema = [
    {
      field: "createdDate",
      headerName: t("pricingChanges.createdDate"),
      flex: 1,
    },
    {
      field: "pricingChangesAction",
      headerName: t("pricingChanges.action"),
      flex: 0.5,
    },
    {
      field: "location",
      headerName: t("pricingChanges.location"),
      flex: 1,
    },
    {
      field: "description",
      headerName: t("pricingChanges.description"),
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value || ""} arrow>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    ...(isLoggedInUserApprover
      ? [
        {
          field: "actionBy",
          headerName: t("pricingChanges.requestedBy"),
          flex: 1,
        },
      ]
      : []),
    {
      field: "status",
      headerName: t("pricingChanges.status"),
      flex: 1,
    },
    {
      field: "approver",
      headerName: t("pricingChanges.approver"),
      flex: 1,
    },
    {
      field: "action",
      headerName: t("pricingChanges.changes"),
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            <Box width={25} />
            {
              <>
                <GridRowAction
                  width={25}
                  height={25}
                  handleSubmit={() => handleShowSelectedPricingChangesDetails(params.row.recordResponse)}
                >
                  <SwapHorizonOutlinedIcon />
                </GridRowAction>
              </>
            }
          </>
        );
      },
    },
    {
      field: "recordResponse",
      hide: true,
    }
  ];

  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={pricingChangesInfoRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalPricingChangesInfoRowCount}
        toolbarHeader={t("pricingChanges.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
        showDateFilter={showDateFilter}
        onApplyDateFilter={onApplyDateFilter}
        onClearFilter={onClearFilter}
        selectedFromDate={selectedFromDate}
        selectedToDate={selectedToDate}
      />
      <SendEmailPopup
        isOpen={popupOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm} />
    </FormPage>
  );
};

export default PricingChangesListForm;
